/**
 * HR & Staff Management Page
 * Staff listing, attendance tracking, and management
 * Connected to backend API
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Users, UserPlus, Search, Clock, Calendar, Award, Mail, Phone,
  RefreshCcw, Loader2, Building2, Shield, MoreHorizontal, CheckCircle,
  XCircle, AlertTriangle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Role badges
const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700',
  manager: 'bg-purple-100 text-purple-700',
  supervisor: 'bg-blue-100 text-blue-700',
  staff: 'bg-green-100 text-green-700',
  cashier: 'bg-amber-100 text-amber-700',
  chef: 'bg-orange-100 text-orange-700',
  server: 'bg-cyan-100 text-cyan-700',
};

export function HRPage() {
  const { currentOutlet } = useAuth();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [activeTab, setActiveTab] = useState('staff');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [staffForm, setStaffForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
  });

  // Fetch staff
  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('is_active', statusFilter === 'active');
      
      const response = await api.get(`/api/users?${params.toString()}`);
      setStaff(response.data || []);
    } catch (err) {
      console.error('Failed to fetch staff:', err);
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await api.get('/api/users/roles');
      setRoles(response.data || []);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
    fetchRoles();
  }, [fetchStaff, fetchRoles]);

  // Refresh on search/filter change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStaff();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter]);

  // Create staff member
  const handleCreateStaff = async () => {
    if (!staffForm.email || !staffForm.first_name || !staffForm.password) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/api/users', staffForm);
      toast.success('Staff member created successfully');
      setCreateDialogOpen(false);
      setStaffForm({ email: '', first_name: '', last_name: '', phone: '', password: '' });
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create staff');
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle staff active status
  const handleToggleStatus = async (staffMember) => {
    try {
      await api.patch(`/api/users/${staffMember.id}`, {
        is_active: !staffMember.is_active,
      });
      toast.success(`Staff ${staffMember.is_active ? 'deactivated' : 'activated'}`);
      fetchStaff();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Stats
  const stats = {
    total: staff.length,
    active: staff.filter(s => s.is_active).length,
    inactive: staff.filter(s => !s.is_active).length,
  };

  const getInitials = (firstName, lastName) => {
    return `${(firstName?.[0] || '').toUpperCase()}${(lastName?.[0] || '').toUpperCase()}`;
  };

  const getRoleBadge = (role) => {
    const colorClass = ROLE_COLORS[role?.toLowerCase()] || 'bg-gray-100 text-gray-700';
    return (
      <Badge className={cn(colorClass, 'text-xs')}>
        {role || 'Staff'}
      </Badge>
    );
  };

  return (
    <div data-testid="hr-page" className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl text-brand-charcoal">HR & Staff Management</h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">
            {currentOutlet?.name || 'All Outlets'} - Manage your team
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchStaff}>
            <RefreshCcw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button 
            className="bg-brand-orange hover:bg-brand-orange/90" 
            size="sm"
            onClick={() => setCreateDialogOpen(true)}
            data-testid="add-staff-btn"
          >
            <UserPlus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Staff</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Staff</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active}</p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <XCircle className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.inactive}</p>
                <p className="text-sm text-gray-500">Inactive</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="staff" data-testid="tab-staff">Staff Directory</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles & Permissions</TabsTrigger>
        </TabsList>

        {/* Staff Tab */}
        <TabsContent value="staff" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="staff-search"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : staff.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No staff found</p>
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    Add your first team member
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {staff.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center gap-4 p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                        data-testid={`staff-${member.id}`}
                      >
                        {/* Avatar */}
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback className="bg-brand-orange text-white">
                            {getInitials(member.first_name, member.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        
                        {/* Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              {member.first_name} {member.last_name}
                            </p>
                            {getRoleBadge(member.role)}
                            {!member.is_active && (
                              <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {member.email}
                            </span>
                            {member.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {member.phone}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedStaff(member);
                              setViewDialogOpen(true);
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={member.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                            onClick={() => handleToggleStatus(member)}
                          >
                            {member.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Roles & Permissions</CardTitle>
              <CardDescription>Manage access levels for your team</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Admin */}
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Shield className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium">Administrator</p>
                      <p className="text-sm text-gray-500">Full system access</p>
                    </div>
                    <Badge className="ml-auto bg-red-100 text-red-700">
                      {staff.filter(s => s.role?.toLowerCase() === 'admin').length} users
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">
                    Can manage all settings, users, outlets, and view all reports
                  </p>
                </div>
                
                {/* Manager */}
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Building2 className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">Manager</p>
                      <p className="text-sm text-gray-500">Outlet management</p>
                    </div>
                    <Badge className="ml-auto bg-purple-100 text-purple-700">
                      {staff.filter(s => s.role?.toLowerCase() === 'manager').length} users
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">
                    Can manage assigned outlet operations, staff schedules, and approvals
                  </p>
                </div>
                
                {/* Staff */}
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Staff</p>
                      <p className="text-sm text-gray-500">Daily operations</p>
                    </div>
                    <Badge className="ml-auto bg-green-100 text-green-700">
                      {staff.filter(s => !['admin', 'manager'].includes(s.role?.toLowerCase())).length} users
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">
                    Can access POS, take orders, and perform daily tasks
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Staff Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-brand-orange" />
              Add Staff Member
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  placeholder="First name"
                  value={staffForm.first_name}
                  onChange={(e) => setStaffForm({ ...staffForm, first_name: e.target.value })}
                  data-testid="staff-first-name"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  placeholder="Last name"
                  value={staffForm.last_name}
                  onChange={(e) => setStaffForm({ ...staffForm, last_name: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="email@company.com"
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                data-testid="staff-email"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                placeholder="+47 XXX XX XXX"
                value={staffForm.phone}
                onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Temporary Password *</Label>
              <Input
                type="password"
                placeholder="Initial password"
                value={staffForm.password}
                onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                data-testid="staff-password"
              />
              <p className="text-xs text-gray-500 mt-1">Staff will be asked to change on first login</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateStaff} 
              disabled={submitting}
              className="bg-brand-orange hover:bg-brand-orange/90"
              data-testid="create-staff-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Staff Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Staff Details</DialogTitle>
          </DialogHeader>
          {selectedStaff && (
            <div className="space-y-4 py-4">
              {/* Profile Header */}
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={selectedStaff.avatar_url} />
                  <AvatarFallback className="bg-brand-orange text-white text-xl">
                    {getInitials(selectedStaff.first_name, selectedStaff.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-xl font-bold">
                    {selectedStaff.first_name} {selectedStaff.last_name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    {getRoleBadge(selectedStaff.role)}
                    <Badge variant={selectedStaff.is_active ? 'default' : 'secondary'}>
                      {selectedStaff.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{selectedStaff.email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{selectedStaff.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Locale</p>
                  <p className="font-medium">{selectedStaff.locale || 'en'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Timezone</p>
                  <p className="font-medium">{selectedStaff.timezone || 'Europe/Oslo'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">
                    {selectedStaff.created_at 
                      ? new Date(selectedStaff.created_at).toLocaleDateString()
                      : '-'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Last Login</p>
                  <p className="font-medium">
                    {selectedStaff.last_login_at 
                      ? new Date(selectedStaff.last_login_at).toLocaleString()
                      : 'Never'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default HRPage;
